import { Link } from 'react-router-dom'
import { useHomepage } from '../../hooks/useHomepage'
import {
  StatsSection,
  FeaturedJobs,
  FeaturedExperts,
  TestimonialsSection,
  TrustedBy,
  CategoriesSection,
} from '../../components/home'

export default function HomePage() {
  const { data, loading } = useHomepage()

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-400 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Ireland's Verified IT Experts
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Connect with skill-verified IT professionals for jobs, contracts, and projects.
              Every expert is assessed with adaptive testing.
            </p>

            {/* Search-style CTA */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="flex flex-col sm:flex-row gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-2">
                <Link
                  to="/jobs"
                  className="flex-1 bg-white text-primary-700 hover:bg-gray-50 rounded-lg px-6 py-4 font-semibold text-center transition-colors"
                >
                  Browse Jobs
                </Link>
                <Link
                  to="/experts"
                  className="flex-1 bg-accent-500 text-white hover:bg-accent-600 rounded-lg px-6 py-4 font-semibold text-center transition-colors"
                >
                  Find Experts
                </Link>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login?type=company"
                className="text-white hover:text-primary-200 font-medium flex items-center justify-center gap-2"
              >
                <span>I'm Hiring</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <span className="hidden sm:block text-primary-300">|</span>
              <Link
                to="/login?type=expert"
                className="text-white hover:text-primary-200 font-medium flex items-center justify-center gap-2"
              >
                <span>I'm an Expert</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      {data?.stats && <TrustedBy stats={data.stats} />}

      {/* Stats Section */}
      {data?.stats && <StatsSection stats={data.stats} />}

      {/* Featured Jobs Section */}
      {!loading && data?.featured_jobs && data.featured_jobs.length > 0 && (
        <FeaturedJobs jobs={data.featured_jobs} />
      )}

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">How It Works</h2>
            <p className="mt-2 text-gray-600">A streamlined process for hiring verified talent</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Skill Verification</h3>
              <p className="text-gray-600">
                Experts complete adaptive assessments that verify their skill levels from Beginner to Expert.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-accent-100 text-accent-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Connect & Contract</h3>
              <p className="text-gray-600">
                Companies post jobs, experts apply, and contracts are managed with milestone-based payments.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Secure Payments</h3>
              <p className="text-gray-600">
                Escrow-protected payments ensure experts get paid and companies get quality work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Experts Section */}
      {!loading && data?.featured_experts && data.featured_experts.length > 0 && (
        <FeaturedExperts experts={data.featured_experts} />
      )}

      {/* Categories Section */}
      {!loading && data?.categories && data.categories.length > 0 ? (
        <CategoriesSection categories={data.categories} />
      ) : (
        // Fallback to static categories if API data not available
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900">Expert Categories</h2>
              <p className="mt-2 text-gray-600">Find verified experts across all IT disciplines</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: 'Development', slug: 'Dev', icon: 'code' },
                { name: 'DevOps & Cloud', slug: 'DevOps', icon: 'cloud' },
                { name: 'Security', slug: 'Security', icon: 'shield' },
                { name: 'Data & Analytics', slug: 'Data', icon: 'database' },
                { name: 'AI & Machine Learning', slug: 'AI/ML', icon: 'brain' },
                { name: 'Cloud Architecture', slug: 'Cloud', icon: 'server' },
                { name: 'Product & Design', slug: 'Product', icon: 'palette' },
                { name: 'Mobile Development', slug: 'Mobile', icon: 'smartphone' },
              ].map((category) => (
                <Link
                  key={category.slug}
                  to={`/experts?category=${category.slug}`}
                  className="bg-white rounded-xl p-6 text-center hover:shadow-md transition-all hover:-translate-y-1"
                >
                  <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900">{category.name}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {!loading && data?.testimonials && data.testimonials.length > 0 && (
        <TestimonialsSection testimonials={data.testimonials} />
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-700 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Join Ireland's fastest-growing IT talent marketplace
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login?type=company"
              className="btn bg-white text-primary-700 hover:bg-gray-100 text-lg px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              I'm Hiring
            </Link>
            <Link
              to="/login?type=expert"
              className="btn bg-accent-500 text-white hover:bg-accent-600 text-lg px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              I'm an Expert
            </Link>
          </div>
        </div>
      </section>

      {/* Loading skeleton for API data */}
      {loading && (
        <div className="fixed bottom-4 right-4 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg text-sm">
          Loading content...
        </div>
      )}
    </div>
  )
}
